'use client';

import { Button, Typography } from '@capsule/consumer-design-system';

import { useTranslations } from 'next-intl';
import { css } from 'styled-components';

import LinkButton from 'src/app/components/Elements/Button/LinkButton';
import { clearRxHIPAA, clearRxPrivacy, clearRxTerms } from 'src/app/router/paths';

import { ClearRxLayout } from '../../ClearRxLayout';
import { medicationItems } from '../constants';
import { Capitalize, StyledMedCard } from '../styles';

import { StyledMainPageContent } from './styles';

type PharmacyHubWelcomeProps = {
  doctor: string;
  patient: string;
  handleStartConfirmPharmacyClick: () => void;
};

export const PharmacyHubWelcome = (props: PharmacyHubWelcomeProps) => {
  const { doctor, patient, handleStartConfirmPharmacyClick } = props;
  const t = useTranslations('pharmacyHubJourney.welcome');

  return (
    <StyledMainPageContent>
      <ClearRxLayout>
        <Typography
          variant="heading1"
          css={css`
            margin-bottom: ${({ theme }) => theme.space.s4};
          `}
        >
          <Capitalize>{t('title', { patient })}</Capitalize>
        </Typography>
        <Typography
          variant="body"
          css={css`
            margin-bottom: ${({ theme }) => theme.space.s6};
          `}
        >
          {t('subtitle', { doctor })}
        </Typography>
        {medicationItems.map(med => (
          <StyledMedCard>
            <Typography variant={'heading3'}>
              {med.name} {med.dosage}
            </Typography>
            <Typography variant={'body'}>
              {t('quantityFormat', {
                count: med.quantity.split(' ')[0],
                unit: parseInt(med.quantity.split(' ')[0]) === 1 ? t('tabletSingular') : t('tabletsPlural'),
              })}
            </Typography>
            <Typography
              variant={'small'}
              css={css`
                color: ${({ theme }) => theme.colors.capsuleGreen60};
              `}
            >
              {med.refillsRemaining === 1
                ? t('refillText', { count: med.refillsRemaining })
                : t('refillsText', { count: med.refillsRemaining })}
            </Typography>
          </StyledMedCard>
        ))}
        <Button
          onClick={handleStartConfirmPharmacyClick}
          isFullWidth
          variant="contained-secondary-lg"
          css={css`
            margin-top: ${({ theme }) => theme.space.s7};
          `}
        >
          {t('continueButton')}
        </Button>
        <Typography
          variant="body"
          css={css`
            margin-top: ${({ theme }) => theme.space.s4};
          `}
        >
          {t('agreementIntro')}{' '}
          <LinkButton style="inlineLink" to={clearRxTerms.toPath()} rel="noopener noreferrer" target="_blank">
            {t('termsText')}
          </LinkButton>
          ,{' '}
          <LinkButton style="inlineLink" to={clearRxPrivacy.toPath()} rel="noopener noreferrer" target="_blank">
            {t('privacyText')}
          </LinkButton>
          , and{' '}
          <LinkButton style="inlineLink" to={clearRxHIPAA.toPath()} rel="noopener noreferrer" target="_blank">
            {t('hipaaText')}
          </LinkButton>
          .
        </Typography>
      </ClearRxLayout>
    </StyledMainPageContent>
  );
};
